import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Save } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { api, type DepartmentRef, type MailRecipientOption, type UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { SITES_KEY } from '../lib/sites';
import { PeoplePicker } from './PeoplePicker';
import { Badge, Button, Card, CardHeader, Checkbox, ErrorState, LoadingBlock, useToast } from './ui';

/** The most employees picked by name (MAX_PUBLISHER_PEOPLE). */
const MAX_PEOPLE = 500;

interface Publishers {
  people: Array<UserRef & { isActive: boolean }>;
  departments: Array<DepartmentRef & { isActive: boolean }>;
  /** Where every site's address starts. */
  address: string;
}

/**
 * Settings › Sites (operator, 2026-11-03: "let employees who has right to do this
 * from settings department or employee and the admin has full right"): who may
 * publish sites beside the administrators — whole departments, and employees by
 * name. Each looks after the sites they make; administrators look after every one.
 */
export function SitesSettingsPanel() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const pickerId = useId();

  const current = useQuery({ queryKey: [...SITES_KEY, 'publishers'], queryFn: () => api.get<Publishers>('/sites/publishers') });
  // Whom it can pick: every active employee — the list a mail goes to — and the switched-on departments.
  const people = useQuery({
    queryKey: ['mailbox', 'recipients'],
    queryFn: () => api.get<MailRecipientOption[]>('/mailbox/recipients'),
    staleTime: 60_000,
  });
  const departmentOptions = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
  });

  const saved = useMemo(
    () => ({
      userIds: (current.data?.people ?? []).map((p) => p.id),
      departmentIds: (current.data?.departments ?? []).map((d) => d.id),
    }),
    [current.data],
  );
  const [draft, setDraft] = useState<{ userIds: string[]; departmentIds: string[] } | null>(null);
  const picked = draft ?? saved;
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const dirty = draft !== null && (!same(draft.userIds, saved.userIds) || !same(draft.departmentIds, saved.departmentIds));

  // Names for those picked who are not on the lists: someone switched off since, a department switched off.
  const known = useMemo(() => new Map<string, UserRef>((current.data?.people ?? []).map((p) => [p.id, p])), [current.data]);
  const departments = useMemo(() => {
    const list = [...(departmentOptions.data ?? [])].filter((d) => d.isActive !== false);
    for (const d of current.data?.departments ?? []) if (!list.some((x) => x.id === d.id)) list.push(d);
    return list.sort((a, b) => localized(a).localeCompare(localized(b)));
  }, [departmentOptions.data, current.data, localized]);

  const save = useMutation({
    mutationFn: () => api.put('/sites/publishers', picked),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: [...SITES_KEY, 'publishers'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('settings.sitesPublishers')}
        subtitle={t('settings.sitesPublishersNote')}
        action={
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            disabled={!dirty || !current.data}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </Button>
        }
      />
      {current.isLoading ? (
        <LoadingBlock />
      ) : current.error ? (
        <ErrorState error={current.error} onRetry={() => void current.refetch()} />
      ) : (
        <div className="space-y-5">
          <fieldset>
            <legend className="mb-1.5 flex items-center gap-2 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('settings.sitesDepartments')}
              {dirty && <Badge tone="warning">unsaved</Badge>}
            </legend>
            <div className="rounded-md border hairline p-3">
              {!departments.length ? (
                <p className="text-body-sm text-muted">{departmentOptions.isLoading ? t('common.loading') : t('settings.sitesNoDepartments')}</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {departments.map((d) => (
                    <Checkbox
                      key={d.id}
                      checked={picked.departmentIds.includes(d.id)}
                      className={clsx(d.isActive === false && 'opacity-60')}
                      label={`${localized(d)}${d.code ? ` · ${d.code}` : ''}`}
                      onChange={(e) =>
                        setDraft({
                          ...picked,
                          departmentIds: e.target.checked
                            ? [...picked.departmentIds, d.id]
                            : picked.departmentIds.filter((x) => x !== d.id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>
            <span className="mt-1 block text-caption text-faint">{t('settings.sitesDepartmentsHint')}</span>
          </fieldset>
          {/* Not a Field: that is a <label>, and a label around the chips' buttons would press them. */}
          <div>
            <label htmlFor={pickerId} className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">
              {t('settings.sitesPeople')}
            </label>
            <PeoplePicker
              inputId={pickerId}
              people={people.data}
              value={picked.userIds}
              onChange={(userIds) => setDraft({ ...picked, userIds })}
              max={MAX_PEOPLE}
              known={known}
              fullLabel={t('settings.sitesPeopleMax', { n: MAX_PEOPLE })}
            />
            <span className="mt-1 block text-caption text-faint">{t('settings.sitesPeopleHint')}</span>
          </div>
          {current.data && (
            <p className="max-w-3xl text-body-sm text-muted">
              {t('settings.sitesAddress')}{' '}
              <span dir="ltr" className="font-medium text-[var(--text-primary)]">
                {current.data.address}/…
              </span>
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
